import { useState } from 'preact/hooks';
import type { Field } from '../../shared/types';
import { fieldDescription } from '../../shared/answers';
import { questionSections, sectionStepName } from '../../shared/questionRouting';
import { standardQuestions } from '../lib/standardQuestions';
import { RichText } from './RichText';
import { QuestionAnswerPreview } from './QuestionAnswerPreview';
import { Tooltip } from './Tooltip';

/**
 * The permission form's own questions, shown as locked cards above the event's questions. They can't be edited. Each
 * section starts collapsed; clicking anywhere on it, its header or any of its questions, expands or collapses it.
 * `labelled` adds a "Your questions" heading after them, for when the event has questions of its own.
 */
export function StandardQuestions({ event, labelled }: { event: { level: 'stake' | 'ward'; wardNames?: string[] }; labelled: boolean }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const sections = questionSections(standardQuestions(event));
  const toggle = (id: string) => setExpanded(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  return <><ol class="qlist standard-questions" aria-label="Questions every registration asks">
    {sections.flatMap((section) => { const { id, fields: [header, ...questions] } = section, collapsed = !expanded.has(id!); return [
      // The button is the keyboard's way in; a click anywhere else on the card does the same.
      <li class="q section-header locked" key={id!} onClick={() => toggle(id!)}>
        <span class="question-section-tab">{sectionStepName(section)}</span>
        <div class="section-header-row"><span class="question-preview-title">{header.label}</span>
          <div class="section-actions"><Tooltip text={collapsed ? 'Expand section' : 'Collapse section'}>
            <button type="button" class="icon section-collapse" aria-label={`${collapsed ? 'Expand' : 'Collapse'} section: ${header.label}`} aria-expanded={!collapsed} onClick={e => { e.stopPropagation(); toggle(id!); }}>
              <svg width="20" height="24" viewBox="0 0 20 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                {collapsed ? <path d="m6 6 4-4 4 4M6 18l4 4 4-4" /> : <path d="m6 3 4 4 4-4M6 21l4-4 4 4" />}
              </svg>
            </button></Tooltip></div></div>
        <span class="question-preview-description muted small">{fieldDescription(header)} Every registration asks these, and they can’t be changed.</span>
      </li>,
      ...(collapsed ? [] : questions.map(field => <LockedQuestion key={field.id} field={field} onClick={() => toggle(id!)} />)),
    ]; })}
  </ol>{labelled && <h3 class="question-group-label">Your questions</h3>}</>;
}

function LockedQuestion({ field: f, onClick }: { field: Field; onClick: () => void }) {
  return <li class={`q locked${f.type === 'title_text' ? ' title-block' : ''}`} onClick={onClick}>
    <div class="question-toggle">
      <span class="question-preview-title"><RichText text={f.label} />{f.required && <span class="req"> *</span>}</span>
      {fieldDescription(f) && <span class="question-preview-description muted small"><RichText text={fieldDescription(f)!} /></span>}
      <QuestionAnswerPreview field={f} />
    </div>
  </li>;
}
